import type { HTMLAttributes, ReactNode } from 'react';
import React from 'react';
import classNames from 'classnames';
import { SmallLabel } from '$quantum/atoms/SmallLabel';
import { Avatar } from '$quantum/atoms/Avatar';
import type { MessageBubbleProps } from '$quantum/atoms/MessageBubble';
import { MessageBubble } from '$quantum/atoms/MessageBubble';

import styles from './index.module.scss';

export interface ChatConversationMessage {
  from: 'sender' | 'replier';
  message: ReactNode;
  /**
   * @NOTE if we want to use this for an actual conversation widget
   * we need to include user ids, message ids and timestamps
   */
}

export interface ChatConversationData {
  messages: ChatConversationMessage[];
}

export interface ChatConversationParticipant {
  displayName?: string;
  avatarImageSrc?: string;
}

export interface ChatConversationProps extends HTMLAttributes<HTMLDivElement> {
  readonly sender: ChatConversationParticipant;
  readonly replier: ChatConversationParticipant;
  readonly conversation: ChatConversationData;
}

export function ChatConversation({
  sender,
  replier,
  conversation,
  className,
  ...props
}: ChatConversationProps): JSX.Element {
  return (
    <div className={classNames(ChatConversation.displayName, className)} {...props}>
      {conversation.messages.length &&
        conversation.messages.map(({ message, from }, index) => {
          const user = from === 'sender' ? sender : replier;
          const showDisplayName = index === 0 || conversation.messages[index - 1]?.from !== from;
          const showAvatar =
            (index !== 0 && conversation.messages[index - 1]?.from === from) ||
            conversation.messages[index + 1]?.from !== from;
          const showMessageBubbleTail =
            (index !== 0 && conversation.messages[index - 1]?.from === from) ||
            conversation.messages[index + 1]?.from !== from;

          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={(user.displayName || from) + index}
              className={classNames(styles.message, styles[from])}
            >
              {showDisplayName && (
                <SmallLabel className={classNames(styles.SmallLabel, styles[from])}>
                  {user.displayName}
                </SmallLabel>
              )}
              {showAvatar && user.avatarImageSrc && (
                <Avatar
                  className={classNames(styles.Avatar, styles[from])}
                  src={user.avatarImageSrc}
                  name={user.displayName || ''}
                  size="small"
                />
              )}
              <MessageBubble
                className={classNames(styles.MessageBubble, styles[from])}
                type={from as MessageBubbleProps['type']}
                showTail={showMessageBubbleTail}
              >
                {message}
              </MessageBubble>
            </div>
          );
        })}
    </div>
  );
}
ChatConversation.displayName = 'ChatConversation';
